import { useContext, useEffect, useState } from "react";
import squareLogo from "@assets/images/square/square-logo-white.png";
import squareLightLogo from "@assets/images/square/square-logo.png";
import IOnSquareOauthRevokeDTO from "@models/DTO/IOnSquareOauthRevokeDTO";
import IOnSquareOauthStartDTO from "@models/DTO/IOnSquareOauthStartDTO";
import ISquareOauthRevokeDTO from "@models/DTO/ISquareOauthRevokeDTO";
import ISquareOauthStartDTO from "@models/DTO/ISquareOauthStartDTO";
import EndpointsEnum from "@models/EndpointsEnum";
import { OwAccordion } from "@components/shared/OwAccordion/OwAccordion";
import OwBadge from "@components/shared/OwBadge/OwBadge";
import OwButton from "@components/shared/OwButton/OwButton";
import * as _ from "lodash-es";
import SupplierContext from "@contexts/SupplierContext";
import UserContext from "@contexts/UserContext";
import { SocketContext } from "@contexts/SocketContext";
import UiContext from "@contexts/UiContext";
import UIService from "@services/UIService";
import IUiSocketSetup from "@models/IUiSocketSetup";
import ILoadUserDTO from "@models/DTO/ILoadUserDTO";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import IUpdateSupplierIntegrationsDTO from "@models/DTO/IUpdateSupplierIntegrationsDTO";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import { SquareTerminalAddModal } from "./SquareTerminalAddModal/SquareTerminalAddModal";
import ISquareOnGetConnectedTerminalsDTO from "@models/DTO/ISquareOnGetConnectedTerminalsDTO";
import ISquare_Terminal from "@models/ISquare_Terminal";
import ISquareGetConnectedTerminalsDTO from "@models/DTO/ISquareGetConnectedTerminalsDTO";
import ActivityIndicator from "@components/shared/ActivityIndicator/ActivityIndicator";

type Props = {
    updateSupplierModelFn: any;
};

const SquareConnect = (props: Props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const [terminals, setTerminals] = useState<ISquare_Terminal[]>([]);
    const [isActive, setIsActive] = useState<boolean>(false);

    const onSquareOauthStart = (url: IOnSquareOauthStartDTO) => {
        // @ts-expect-error
        window.location = url;
    };

    const onSquareOauthRevoke = (data: IOnSquareOauthRevokeDTO) => {
        const payload: ILoadUserDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
        };

        socket.emit(EndpointsEnum.AppLoadUser, payload);
    };

    const onSquareGetConnectedTerminals = (data: ISquareOnGetConnectedTerminalsDTO) => {
        console.log(data);

        setTerminals(data.terminals);
        setIsActive(false);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.SquareOnOAuthStart, fn: onSquareOauthStart },
        { endpoint: EndpointsEnum.SquareOnOAuthRevoke, fn: onSquareOauthRevoke },
        { endpoint: EndpointsEnum.SquareOnGetConnectedTerminals, fn: onSquareGetConnectedTerminals },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const getConnectedTerminals = () => {
        const payload: ISquareGetConnectedTerminalsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.SquareGetConnectedTerminals, payload);
        setIsActive(true);
    };

    const startSquareConnection = () => {
        const payload: ISquareOauthStartDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.SquareOAuthStart, payload);
    };

    const disconnectSquare = () => {
        const payload: ISquareOauthRevokeDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.SquareOAuthRevoke, payload);
    };

    const toggleSquareTerminals = () => {
        const payload: IUpdateSupplierIntegrationsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            // @ts-expect-error
            Supplier_Integration: {
                has_square_terminals: !supplierModel.Supplier_Integration.has_square_terminals,
            },
        };

        socket.emit(EndpointsEnum.SupplierUpdateSupplierIntegrations, payload);
    };

    const showAddTerminalModal = () => {
        const component = <SquareTerminalAddModal />;

        uiContext.setModalComponent(component);
        uiContext.showModal(true);
    };

    const hasSquare = supplierModel.Supplier_Integration.has_square;
    let logo = squareLogo;

    if (UIService.isMainViewLight()) {
        logo = squareLightLogo;
    }

    return (
        <div className="SquareConnect">
            <OwAccordion
                hideChevron={!hasSquare}
                leftImage={logo}
                leftImageHeight={30}
                titleClick={hasSquare}
                fn={getConnectedTerminals}
                rightElem={
                    <div className={"ms-auto"}>
                        {hasSquare ? (
                            <OwBadge title={"connected"} bgColor={"bg-success"} leftIconClass={"fas fa-check"} />
                        ) : (
                            <OwButton minWidth={150} title={"connect"} fn={startSquareConnection} />
                        )}
                    </div>
                }>
                <div>
                    <div className={hasSquare ? "" : "d-none"}>
                        <div className={"mb-4 text-end"}>
                            <OwButton
                                minWidth={225}
                                className={"btn-danger"}
                                fn={disconnectSquare}
                                leftIconClass={"fas fa-unlink"}
                                title={"disconnect_square"}
                            />
                        </div>
                        <div>
                            <OwToggle
                                name="square_terminals"
                                label="square_terminals"
                                active={!!supplierModel.Supplier_Integration.has_square_terminals}
                                fn={toggleSquareTerminals}
                                labelOnRight={true}
                            />
                        </div>
                        {supplierModel.Supplier_Integration.has_square_terminals && (
                            <div>
                                <div className="mt-4 mb-2">
                                    <OwButton
                                        className="btn-warning"
                                        leftIconClass="fas fa-plus"
                                        title="add_terminal"
                                        fn={showAddTerminalModal}
                                    />
                                </div>
                                {isActive && <ActivityIndicator active={true} />}
                                <div>
                                    {terminals.map((terminal) => {
                                        return (
                                            <div key={terminal.id}>
                                                <OwLabel className="me-2" title="terminal_id" isBold={true} postText=":" />
                                                {terminal.terminal_id} - {terminal.name}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </OwAccordion>
        </div>
    );
};

export default SquareConnect;
